import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Filter, Grid, List, Plus, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAccounts } from '@/store/accountsStore';
import { useTransactionStore } from '@/store/transactionsStore';
import { useAuthStore } from '@/store/authStore';
import AccountCard from '@/components/accounts/AccountCard';
import { TransactionList } from '@/components/transactions/TransactionList';
import TransactionGrid from '@/components/transactions/TransactionGrid';
import TransactionFilters from '@/components/transactions/TransactionFilters';
import AddTransaction from '@/components/transactions/AddTransaction';
import ImportTransactions from '@/components/transactions/ImportTransactions';
import { Account } from '@/types/base';

export default function AccountDetails() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const { accounts } = useAccounts();
  const { fetchTransactions } = useTransactionStore();
  const { user } = useAuthStore();
  const account = accounts.find((a) => a.id === accountId) as Account | undefined;

  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [showImport, setShowImport] = useState(false);

  useEffect(() => {
    if (!account) {
      navigate('/accounts');
    }
  }, [account, navigate]);

  useEffect(() => {
    if (user?.id) {
      fetchTransactions(user.id);
    }
  }, [user?.id, fetchTransactions]);

  if (!account) return null;

  return (
    <div className="flex flex-col h-full">
      <header className="border-b border-gray-200/50 bg-white/80 backdrop-blur-md backdrop-filter">
        <div className="px-6 py-4">
          <button
            onClick={() => navigate('/accounts')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Accounts
          </button>
          
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">{account.name}</h1>
              <p className="text-gray-600">Account transactions and details</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowImport(true)}
                className="glass-button flex items-center"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </button>
              <button className="glass-button flex items-center">
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
              <Button
                onClick={() => setShowAddTransaction(true)}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Transaction
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Account Card */}
        <div className="max-w-md">
          <AccountCard account={account} />
        </div>

        {/* Transactions Section */}
        <Card>
          <div className="p-4 border-b border-gray-100 flex justify-between items-center">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="glass-button flex items-center"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </button>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg ${
                  viewMode === 'list'
                    ? 'bg-primary-50 text-primary-600'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg ${
                  viewMode === 'grid'
                    ? 'bg-primary-50 text-primary-600'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <Grid className="w-5 h-5" />
              </button>
            </div>
          </div>

          {showFilters && <TransactionFilters />}

          <CardContent>
            {viewMode === 'list' ? (
              <TransactionList accountId={account.id} />
            ) : (
              <TransactionGrid accountId={account.id} />
            )}
          </CardContent>
        </Card>
      </div>

      {showAddTransaction && (
        <AddTransaction 
          onClose={() => setShowAddTransaction(false)} 
        />
      )}

      {showImport && (
        <ImportTransactions 
          onClose={() => setShowImport(false)}
          accountId={account.id}
        />
      )}
    </div>
  );
}